<template>
    <div>
        <div v-if="hasSortBy">
            <q-select
                v-model="selectedSortBy"
                transitionShow="jump-up"
                transitionHide="jump-up"
                label="Sort by"
                class="q-pt-md"
                dense
                outlined
                :options="optionsSortBy"
                @update:model-value="updateSortParams"
            />
            <q-select
                v-model="selectedSortDirection"
                transitionShow="jump-up"
                transitionHide="jump-up"
                class="q-pt-md"
                outlined
                dense
                label="Order by"
                :options="optionsSortDirection"
                @update:model-value="updateSortParams"
            />
        </div>
        <div v-else>
            <div v-if="graphType === 'FREQUENCY_AND_DURATION'">
                <q-select
                    v-model="duration"
                    transitionShow="jump-up"
                    transitionHide="jump-up"
                    class="q-pt-md"
                    dense
                    outlined
                    :options="durationOptions"
                    :label="$t('visualization.customPlotPanels.frequencyDuration.selectDuration')"
                    @update:modelValue="updateFreqDuration"
                />
                <q-select
                    v-model="rangeSel"
                    transitionShow="jump-up"
                    transitionHide="jump-up"
                    class="q-pt-md"
                    dense
                    outlined
                    :options="rangeOptions"
                    :label="$t('visualization.customPlotPanels.frequencyDuration.selectRange')"
                    @update:modelValue="updateFreqDuration"
                />
            </div>
            <div v-if="graphType === 'ATTRIBUTE'">
                <q-select
                    v-model="attribute"
                    class="attrSelect q-pb-xs"
                    dense
                    outlined
                    :hint="!numericAttribute ? $t('visualization.attributes.categorical') : $t('visualization.attributes.number')"
                    :options="optionAttributes.map(i => i.name)"
                    :label="$t('visualization.customPlotPanels.attributesInfo.selectAttribute')"
                    @update:modelValue="updateAttribute('attribute')"
                />
                <div
                    v-if="!numericAttribute"
                    class="column"
                >
                    <div class="column">
                        <div class="text-caption text-left q-pt-sm">
                            {{ $t('visualization.customPlotPanels.attributesInfo.sortBy') }}
                        </div>
                        <q-btn-toggle
                            v-model="attributeSortBy"
                            toggleColor="primary"
                            noCaps
                            style="max-width: fit-content;"
                            dense
                            class="q-mt-sm"
                            :options="attributeSortByOptions"
                            @update:modelValue="updateAttribute"
                        />
                    </div>
                    <q-btn-toggle
                        v-model="viewMode"
                        class="q-mt-sm"
                        toggleColor="primary"
                        style="max-width: fit-content;"
                        noCaps
                        dense
                        :options="viewModeOptions"
                        @update:modelValue="updateAttribute"
                    />
                    <q-toggle
                        v-model="eventAttribute"
                        :label="$t('visualization.customPlotPanels.attributesInfo.eventAttribute')"
                        size="xs"
                        class="q-mt-md"
                        dense
                        @update:modelValue="updateAttribute"
                    />
                    <q-select
                        v-if="eventAttribute"
                        ref="activities"
                        v-model="activities"
                        dense
                        outlined
                        :label="$t('visualization.customPlotPanels.attributesInfo.selectActivity')"
                        :multiple="true"
                        :options="optionActivities"
                        class="q-py-md"
                        :rules="[value => validators.required(value.length > 0) || $t('visualization.customPlotPanels.attributesInfo.activityRequired')]"
                        @update:modelValue="handleStandardActivitiesInput"
                    >
                        <template #option="scope">
                            <QItem v-bind="scope.itemProps">
                                <QItemSection>
                                    <QItemLabel>
                                        {{ scope.opt }}
                                    </QItemLabel>
                                </QItemSection>
                                <QItemSection side>
                                    <QToggle
                                        v-model="scope.selected"
                                        :val="scope.opt"
                                        @update:modelValue="handleActivitiesInput(scope.opt)"
                                    />
                                </QItemSection>
                            </QItem>
                        </template>
                    </q-select>
                </div>
                <div
                    v-else
                    class="column q-pa-xs"
                >
                    <q-range
                        v-model="numericRange"
                        :min="numericAttrMin"
                        :max="numericAttrMax"
                        dense
                        class="q-px-md"
                        style="margin-top: 35px;"
                        labelAlways
                        @change="updateAttribute()"
                    />
                    <q-toggle
                        v-model="eventAttribute"
                        :label="$t('visualization.customPlotPanels.attributesInfo.eventAttribute')"
                        size="xs"
                        class="q-pt-md"
                        dense
                        @update:modelValue="updateAttribute"
                    />
                    <q-select
                        v-if="eventAttribute"
                        ref="activities"
                        v-model="activities"
                        dense
                        outlined
                        :label="$t('visualization.customPlotPanels.attributesInfo.selectActivity')"
                        :multiple="true"
                        :options="optionActivities"
                        class="q-py-md"
                        :rules="[value => validators.required(value.length > 0) || $t('visualization.customPlotPanels.attributesInfo.activityRequired')]"
                        @update:modelValue="handleStandardActivitiesInput"
                    >
                        <template #option="scope">
                            <q-item v-bind="scope.itemProps">
                                <q-item-section>
                                    <q-item-label>
                                        {{ scope.opt }}
                                    </q-item-label>
                                </q-item-section>
                                <q-item-section side>
                                    <q-toggle
                                        v-model="scope.selected"
                                        :val="scope.opt"
                                        @update:modelValue="handleActivitiesInput(scope.opt)"
                                    />
                                </q-item-section>
                            </q-item>
                        </template>
                    </q-select>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { validatorsMixin } from '@/mixins'
import { ChartTypes } from "../utils"

export default {
    name: "ChartConfiguration",
    mixins: [validatorsMixin],
    props: {
        graphType: {
            type: String,
            required: true,
        },
        graphProps: {
            type: Object,
            required: true,
        },
        attributes: {
            type: Array,
            required: true,
        },
        categoricalAttributes: {
            type: Array,
            required: true,
        },
        numericAttributes: {
            type: Array,
            required: true,
        },
    },
    emits: ["graphUpdated"],
    data() {
        return {
            // Attribute chart
            optionAttributes: [],
            attribute: null,
            numericAttribute: false,
            numericRange: { min: 0, max: 0 },
            numericAttrMin: null,
            numericAttrMax: null,
            viewMode: 'ABSOLUTE',
            viewModeOptions: [
                {
                    value: 'ABSOLUTE',
                    label: 'abs',
                },
                {
                    value: 'PERCENTAGE',
                    label: '%',
                },
            ],
            eventAttribute: false,
            optionActivities: [],
            attributeSortBy: 'FREQUENCY',
            attributeSortByOptions: [
                {
                    value: 'FREQUENCY',
                    label: this.$t('visualization.customPlotPanels.frequency'),
                },
                {
                    value: 'DURATION',
                    label: this.$t('visualization.customPlotPanels.duration'),
                },
            ],

            // Sort by charts
            selectedSortBy: {},
            selectedSortDirection() {
                return this.graphProps.graphValues.sortDirection || { key: "ASCENDING", label: "Ascending" }
            },
            optionsSortDirection: [
                { key: "ASCENDING", label: "Ascending" },
                { key: "DESCENDING", label: "Descending" },
            ],
            optionsSortBy: [
                { key: "FREQUENCY", label: "Frequency" },
                { key: "DURATION_AVG", label: "Longer Average Duration" },
                { key: "DURATION_MAX", label: "Longer Maximum Duration" },
                { key: "DURATION_MIN", label: "Longer Minimum Duration" },
                { key: "DURATION_STD", label: "Longer standard deviation time" },
            ],

            // Frequency and Duration chart
            duration: {
                key: "MONTH",
                label: this.$t("visualization.customPlotPanels.attributesInfo.month"),
            },
            rangeSel: {
                key: "START",
                label: this.$t("visualization.customPlotPanels.attributesInfo.start"),
            },
            durationOptions: [
                {
                    key: 'MONTH',
                    label: this.$t('visualization.customPlotPanels.attributesInfo.month'),
                },
                {
                    key: 'WEEK',
                    label: this.$t('visualization.customPlotPanels.attributesInfo.week'),
                },
                {
                    key: 'DAY',
                    label: this.$t('visualization.customPlotPanels.attributesInfo.day'),
                },
            ],
            rangeOptions: [
                {
                    key: 'START',
                    label: this.$t('visualization.customPlotPanels.attributesInfo.start'),
                },
                {
                    key: 'END',
                    label: this.$t('visualization.customPlotPanels.attributesInfo.end'),
                },
            ],
        }
    },
    computed: {
        hasSortBy() {
            return [ChartTypes.BAR, ChartTypes.HEATMAP].includes(this.graphType)
        },
    },
    watch: {
        graphProps: {
            handler(newVal) {
                if (this.hasSortBy) {
                    this.handleSortByParams(newVal)
                } else if (this.graphType === "FREQUENCY_AND_DURATION") {
                    this.handleFreqDurationParams(newVal)
                } else if (this.graphType === "ATTRIBUTE") {
                    this.handleAttributeParams(newVal)
                }
            },
            deep: true,
            immediate: true,
        },
    },
    methods: {
        handleActivitiesInput(selected) {
            if (!this.activities.includes(selected)) {
                this.activities.push(selected)
            } else {
                const index = this.activities.indexOf(selected)
                if (index !== -1) {
                    this.activities.splice(index, 1)
                }
            }
            clearTimeout(this.activitiesTimer)
            this.activitiesTimer = setTimeout(() => {
                this.updateAttribute()
            }, 500)
        },
        handleStandardActivitiesInput() {
            clearTimeout(this.activitiesTimer)
            this.activitiesTimer = setTimeout(() => {
                this.updateAttribute()
            }, 500)
        },

        updateSortParams() {
            const params = {
                index: this.graphProps.i,
                name: this.graphProps.name,
                sortBy: this.selectedSortBy.key,
                sortDirection: this.selectedSortDirection.key,
            }
            this.$eventBus.emit(`updateSortParams-${params.index}`, params)
            this.$emit("graphUpdated", params)
        },
        updateFreqDuration() {
            const params = {
                index: this.graphProps.i,
                name: this.graphProps.name,
                duration: this.duration.key,
                caseType: this.rangeSel.key,
            }
            this.$eventBus.emit(`updateFreqDuration-${params.index}`, params)
            this.$emit("graphUpdated", params)
        },
        updateAttribute(source) {
            let fetchParams = {}
            let chartParams = {}
            const typeAttr = this.optionAttributes.find((obj) => obj.name === this.attribute)
            if (typeAttr.type === 'STRING' || typeAttr.type === 'BOOLEAN') {
                this.numericAttribute = false
                fetchParams = {
                    sortBy: this.attributeSortBy,
                    limit: 15,
                    start: 0,
                }
                chartParams = {
                    sortBy: this.attributeSortBy,
                }
            } else {
                this.numericAttribute = true
                if (source === 'attribute') {
                    const numAttr = this.numericAttributes.find((a) => a.name === this.attribute)
                    this.numericAttrMin = Number(numAttr.values.at(0))
                    this.numericAttrMax = Number(numAttr.values.at(1))
                    this.numericRange = { min: this.numericAttrMin, max: this.numericAttrMax }
                    chartParams = {
                        numericRange: this.numericRange,
                        numericAttrMin: this.numericAttrMin,
                        numericAttrMax: this.numericAttrMax,
                    }
                }
                chartParams = {
                    numericRange: this.numericRange,
                    numericAttrMin: this.numericAttrMin,
                    numericAttrMax: this.numericAttrMax,
                }
                fetchParams = {
                    startValue: this.numericRange.min,
                    endValue: this.numericRange.max,
                }
            }

            const params = {
                index: this.graphProps.i,
                name: this.graphProps.name,
                attribute: this.attribute,
                eventAttribute: this.eventAttribute,
                numericAttribute: this.numericAttribute,
                activities: this.activities,
                viewMode: this.viewMode,
                ...fetchParams,
            }
            this.$eventBus.emit(`updateAttribute-${params.index}`, params)

            const attributesInfo = {
                optionAttributes: this.optionAttributes,
                optionActivities: this.optionActivities,
                ...chartParams,
            }

            this.$emit("graphUpdated", {
                ...attributesInfo,
                ...params,
            })
        },
        handleAttributeParams(newVal) {
            const graphValues = JSON.parse(newVal.graphValues)
            if (Object.keys(graphValues).length !== 0) {
                this.attribute = graphValues.attribute
                this.optionAttributes = graphValues.optionAttributes || []
                this.optionActivities = graphValues.optionActivities || []
                this.viewMode = graphValues.viewMode || this.viewMode
                this.eventAttribute = graphValues.eventAttribute || false
                if (this.eventAttribute) {
                    this.activities = graphValues.activities || []
                }
                this.numericAttribute = graphValues.numericAttribute || false
                if (this.numericAttribute) {
                    this.numericAttrMin = graphValues.numericAttrMin
                    this.numericAttrMax = graphValues.numericAttrMax
                    this.numericRange = graphValues.numericRange
                }
            }
        },

        handleSortByParams(newVal) {
            try {
                const graphValues = JSON.parse(newVal.graphValues)
                this.selectedSortBy = this.optionsSortBy.find((i) => i.key === graphValues.sortBy)
                this.selectedSortDirection = this.optionsSortDirection.find((i) => i.key === graphValues.sortDirection)
            } catch (error) {
                console.error("Error parsing graphValues:", error)
            }
        },
        handleFreqDurationParams(newVal) {
            try {
                const graphValues = JSON.parse(newVal.graphValues || "{}")
                this.duration = this.durationOptions.find((i) => i.key === graphValues.duration) || this.durationOptions[0]
                this.rangeSel = this.rangeOptions.find((i) => i.key === graphValues.caseType) || this.rangeOptions[0]
            } catch (error) {
                console.error("Error parsing graphValues:", error)
                // eslint-disable-next-line prefer-destructuring
                this.duration = this.durationOptions[0]
                // eslint-disable-next-line prefer-destructuring
                this.rangeSel = this.rangeOptions[0]
            }
        },
    },
}
</script>
